import { LookupDomainResponse } from 'business-dashboard/src/models/common';
import { dateFormats } from '../constants';
import {
    DashboardLocale,
    ErrorType,
    Group,
    PartnerLogoDetails,
    NewsletterSignUpObject,
    SensorThresholds,
    Units,
    DeviceDetailsResponse,
} from '../models/commonTypeScript';
import { CommonActionRequestType, CommonRequestType } from '../reducers/requestReducer';
import { RequestActionType } from './requestActions';
import { SelectGroup, SelectGroupSuccess } from './selectGroupActions';

export const FETCH_USER_INFO = 'FETCH_USER_INFO';
export interface FetchUserInfo {
    type: typeof FETCH_USER_INFO;
}
export const fetchUserInfo = (): FetchUserInfo => ({ type: FETCH_USER_INFO });

export const FETCH_USER_INFO_SUCCESS = 'FETCH_USER_INFO_SUCCESS';
interface UserSettingsPayload {
    name: string;
    email: string;
    dateFormat: keyof typeof dateFormats;
    units: Units;
    language: DashboardLocale;
    intercomUserHash: string;
    intercomUserHashB2B?: string;
    userId: string;
    groups: Group[];
}

export interface FetchUserInfoSuccess extends UserSettingsPayload {
    type: typeof FETCH_USER_INFO_SUCCESS;
}
export const fetchUserInfoSuccess = (payload: UserSettingsPayload): FetchUserInfoSuccess => ({
    type: FETCH_USER_INFO_SUCCESS,
    ...payload,
});

export const FETCH_PARTNER_PROPS_SUCCESS = 'FETCH_PARTNER_PROPS_SUCCESS';
export interface FetchPartnerPropsSuccess {
    type: typeof FETCH_PARTNER_PROPS_SUCCESS;
    partnerProps: PartnerLogoDetails;
}
export const fetchPartnerPropsSuccess = (partnerProps: PartnerLogoDetails): FetchPartnerPropsSuccess => ({
    type: FETCH_PARTNER_PROPS_SUCCESS,
    partnerProps,
});

export const FETCH_USER_INFO_ERROR = 'FETCH_USER_INFO_ERROR';
export interface FetchUserInfoError {
    type: typeof FETCH_USER_INFO_ERROR;
    error: ErrorType;
}
export const fetchUserInfoError = (error: ErrorType): FetchUserInfoError => ({ type: FETCH_USER_INFO_ERROR, error });

export const UPDATE_USER_SETTINGS = 'UPDATE_USER_SETTINGS';
export interface UpdateUserSettingsPayload {
    name?: string;
    units: Units;
    dateFormat?: string;
    language?: DashboardLocale;
    rfRegion?: string;
}
export interface UpdateUserSettings {
    type: typeof UPDATE_USER_SETTINGS;
    payload: UpdateUserSettingsPayload;
}
export const updateUserSettings = (payload: UpdateUserSettingsPayload): UpdateUserSettings => ({
    type: UPDATE_USER_SETTINGS,
    payload,
});

export const UPDATE_USER_SETTINGS_SUCCESS = 'UPDATE_USER_SETTINGS_SUCCESS';
export interface UpdateUserSettingsSuccess {
    type: typeof UPDATE_USER_SETTINGS_SUCCESS;
    payload: UpdateUserSettingsPayload;
}
export const updateUserSettingsSuccess = (payload: UpdateUserSettingsPayload): UpdateUserSettingsSuccess => ({
    type: UPDATE_USER_SETTINGS_SUCCESS,
    payload,
});

export const UPDATE_USER_SETTINGS_ERROR = 'UPDATE_USER_SETTINGS_ERROR';
export interface UpdateUserSettingsError {
    type: typeof UPDATE_USER_SETTINGS_ERROR;
    error: ErrorType;
}
export const updateUserSettingsError = (error: ErrorType): UpdateUserSettingsError => ({
    type: UPDATE_USER_SETTINGS_ERROR,
    error,
});

export const CHANGE_PASSWORD = 'CHANGE_PASSWORD';
export interface ChangePassword {
    type: typeof CHANGE_PASSWORD;
    payload: { oldPassword: string; newPassword: string };
}
export const changePassword = (payload: { oldPassword: string; newPassword: string }): ChangePassword => ({
    type: CHANGE_PASSWORD,
    payload,
});

export const CHANGE_PASSWORD_SUCCESS = 'CHANGE_PASSWORD_SUCCESS';
export interface ChangePasswordSuccess {
    type: typeof CHANGE_PASSWORD_SUCCESS;
}
export const changePasswordSuccess = (): ChangePasswordSuccess => ({ type: CHANGE_PASSWORD_SUCCESS });

export const CHANGE_PASSWORD_ERROR = 'CHANGE_PASSWORD_ERROR';
export interface ChangePasswordError {
    type: typeof CHANGE_PASSWORD_ERROR;
    error: ErrorType;
}
export const changePasswordError = (error: ErrorType): ChangePasswordError => ({ type: CHANGE_PASSWORD_ERROR, error });

export const SIGN_UP_NEWSLETTER = 'SIGN_UP_NEWSLETTER';
export interface SignUpNewsletter {
    type: typeof SIGN_UP_NEWSLETTER;
    payload: NewsletterSignUpObject;
}
export const signUpNewsletter = (payload: NewsletterSignUpObject): SignUpNewsletter => ({
    type: SIGN_UP_NEWSLETTER,
    payload,
});

export const SET_MOBILE_SETTINGS_MENU_IS_OPEN = 'SET_MOBILE_SETTINGS_MENU_IS_OPEN';
export interface SetMobileSettingsMenuIsOpen {
    type: typeof SET_MOBILE_SETTINGS_MENU_IS_OPEN;
    isOpen: boolean;
}
export const setMobileSettingsMenuIsOpen = (isOpen: boolean): SetMobileSettingsMenuIsOpen => ({
    type: SET_MOBILE_SETTINGS_MENU_IS_OPEN,
    isOpen,
});

export enum SettingsActionType {
    FetchDeviceDetails = 'FETCH_DEVICE_DETAILS/INIT',
    FetchDeviceDetailsSuccess = 'FETCH_DEVICE_DETAILS_SUCCESS',
}
export interface FetchDeviceDetails {
    requestActionType: RequestActionType.Init;
    requestType: CommonRequestType.FetchDeviceDetails;
    type: SettingsActionType.FetchDeviceDetails;
}
export const fetchDeviceDetails = (): FetchDeviceDetails => ({
    requestActionType: RequestActionType.Init as RequestActionType.Init,
    requestType: CommonRequestType.FetchDeviceDetails,
    type: SettingsActionType.FetchDeviceDetails,
});

export interface FetchDeviceDetailsSuccess {
    type: SettingsActionType.FetchDeviceDetailsSuccess;
    payload: {
        thresholds: { [sensor: string]: SensorThresholds };
        devices: { [deviceType: string]: DeviceDetailsResponse };
    };
}
export const fetchDeviceDetailsSuccess = (payload: {
    thresholds: { [sensor: string]: SensorThresholds };
    devices: { [deviceType: string]: DeviceDetailsResponse };
}): FetchDeviceDetailsSuccess => ({
    type: SettingsActionType.FetchDeviceDetailsSuccess,
    payload,
});

export enum ConsentAction {
    DeclineManagedByPartnerConsent = 'DECLINE_MANAGED_BY_PARTNER_CONSENT/INIT',
    DeclineManagedByPartnerConsentSuccess = 'DECLINE_MANAGED_BY_PARTNER_CONSENT_SUCCESS',
    GiveConsent = 'GIVE_CONSENT/INIT',
    GiveConsentSuccess = 'GIVE_CONSENT_SUCCESS',
}

export interface GiveConsentPayload {
    userGroupId: string;
    consentId: string;
}

export interface GiveConsent extends CommonActionRequestType {
    requestActionType: RequestActionType.Init;
    requestType: CommonRequestType.GiveConsent;
    type: ConsentAction.GiveConsent;
    payload: GiveConsentPayload;
}

export const giveConsent = (payload: GiveConsentPayload): GiveConsent => ({
    requestActionType: RequestActionType.Init as RequestActionType.Init,
    requestType: CommonRequestType.GiveConsent,
    type: ConsentAction.GiveConsent,
    payload,
});

export interface GiveConsentSuccess {
    type: ConsentAction.GiveConsentSuccess;
}

export const giveConsentSuccess = (): GiveConsentSuccess => ({
    type: ConsentAction.GiveConsentSuccess,
});

export interface DeclineManagedByPartnerConsentPayload {
    userGroupId: string;
}

export interface DeclineManagedByPartnerConsent extends CommonActionRequestType {
    requestActionType: RequestActionType.Init;
    requestType: CommonRequestType.DeclineManagedByPartnerConsent;
    type: ConsentAction.DeclineManagedByPartnerConsent;
    payload: DeclineManagedByPartnerConsentPayload;
}

export const declineManagedByPartnerConsent = (
    payload: DeclineManagedByPartnerConsentPayload
): DeclineManagedByPartnerConsent => ({
    requestActionType: RequestActionType.Init as RequestActionType.Init,
    requestType: CommonRequestType.DeclineManagedByPartnerConsent,
    type: ConsentAction.DeclineManagedByPartnerConsent,
    payload,
});

export interface DeclineManagedByPartnerConsentSuccess {
    type: ConsentAction.DeclineManagedByPartnerConsentSuccess;
}

export const declineManagedByPartnerConsentSuccess = (): DeclineManagedByPartnerConsentSuccess => ({
    type: ConsentAction.DeclineManagedByPartnerConsentSuccess,
});

export enum DomainLookupActionType {
    LookupDomainInit = 'LOOKUP_DOMAIN/INIT',
    LookupDomainSuccess = 'LOOKUP_DOMAIN_SUCCESS',
}

export interface LookupDomain extends CommonActionRequestType {
    type: DomainLookupActionType.LookupDomainInit;
    emailDomain: string;
}
export const lookupDomain = (emailDomain: string): LookupDomain => ({
    requestActionType: RequestActionType.Init,
    requestType: CommonRequestType.LookupDomain,
    type: DomainLookupActionType.LookupDomainInit,
    emailDomain,
});

export interface LookupDomainSuccess {
    type: DomainLookupActionType.LookupDomainSuccess;
    response: LookupDomainResponse;
}
export const lookupDomainSuccess = (response: LookupDomainResponse): LookupDomainSuccess => ({
    type: DomainLookupActionType.LookupDomainSuccess,
    response,
});

export type SettingsReducerActions =
    | FetchUserInfoSuccess
    | UpdateUserSettings
    | UpdateUserSettingsSuccess
    | UpdateUserSettingsError
    | ChangePassword
    | ChangePasswordSuccess
    | ChangePasswordError
    | SelectGroup
    | SelectGroupSuccess
    | FetchPartnerPropsSuccess
    | SetMobileSettingsMenuIsOpen
    | DeclineManagedByPartnerConsent
    | GiveConsent
    | DeclineManagedByPartnerConsentSuccess
    | LookupDomain
    | LookupDomainSuccess;
